import { TimeTrackerEvents } from '../types';
import { getAbstractAccountAddress } from '../utils/getAbstractAccountAddress';
import { getSignerAddress } from '../utils/getSignerAddress';
import { getFunctionSigHash } from '../utils/getFunctionSigHash';
import { encodeDataForLinkingSmartAccount } from '../utils/encodeDataForLinkingSmartAccount';
export async function createSpace(params) {
    const { spaceDapp, timeTracker, fnArgs, smartAccount, paymasterProxyUrl, paymasterProxyAuthSecret, } = params;
    if (!spaceDapp) {
        throw new Error('spaceDapp is required');
    }
    const [createpaceParams, signer] = fnArgs;
    const endGetAA = timeTracker?.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'userops_get_abstract_account_address');
    const abstractAccountAddress = await getAbstractAccountAddress({
        rootKeyAddress: await getSignerAddress(signer),
        newAccountImplementationType: smartAccount.type,
        paymasterProxyUrl,
        paymasterProxyAuthSecret,
    });
    endGetAA?.();
    if (!abstractAccountAddress) {
        throw new Error('abstractAccountAddress is required');
    }
    // stackup-worker identifier,
    // fn is overloaded, but stackup-worker only checks "createSpaceWithPrepay"
    const createSpaceFnName = 'createSpaceWithPrepay';
    const createSpaceShim = spaceDapp.spaceRegistrar.CreateSpace;
    const callDataCreateSpace = createSpaceShim.encodeFunctionData('createSpace((string,string,string,string,((string,string,uint256,uint256,uint64,address,address,uint256,address),(bool,address[],bytes,bool),string[]),(string)))', [
        {
            name: createpaceParams.spaceName,
            uri: createpaceParams.uri,
            shortDescription: createpaceParams.shortDescription ?? '',
            longDescription: createpaceParams.longDescription ?? '',
            membership: createpaceParams.membership,
            channel: {
                metadata: createpaceParams.channelName || '',
            },
        },
    ]);
    const toContractAddress = createSpaceShim.address;
    const endLinkCheck = timeTracker?.startMeasurement(TimeTrackerEvents.CREATE_SPACE, 'userops_check_if_linked');
    const hasLinkedWallet = await spaceDapp.walletLink.checkIfLinked((await signer.getAddress()), abstractAccountAddress);
    endLinkCheck?.();
    if (hasLinkedWallet) {
        const functionHashForPaymasterProxy = getFunctionSigHash(createSpaceShim.interface, createSpaceFnName);
        return await smartAccount.sendUserOp({
            toAddress: toContractAddress,
            callData: callDataCreateSpace,
            signer,
            spaceId: undefined,
            functionHashForPaymasterProxy,
        });
    }
    else {
        // wallet isn't linked, create a user op that both links and creates the space
        const functionHashForPaymasterProxy = getFunctionSigHash(createSpaceShim.interface, 'createSpace_linkWallet');
        const callDataForLinkingSmartAccount = await encodeDataForLinkingSmartAccount(spaceDapp, signer, abstractAccountAddress);
        return await smartAccount.sendUserOp({
            toAddress: [spaceDapp.walletLink.address, toContractAddress],
            callData: [callDataForLinkingSmartAccount, callDataCreateSpace],
            signer,
            spaceId: undefined,
            functionHashForPaymasterProxy,
        });
    }
}
export async function createLegacySpace(params) {
    const { spaceDapp, fnArgs, smartAccount, paymasterProxyUrl, paymasterProxyAuthSecret, } = params;
    if (!spaceDapp) {
        throw new Error('spaceDapp is required');
    }
    const [createLegacySpaceParams, signer] = fnArgs;
    const spaceInfo = {
        name: createLegacySpaceParams.spaceName,
        uri: createLegacySpaceParams.uri,
        shortDescription: createLegacySpaceParams.shortDescription ?? '',
        longDescription: createLegacySpaceParams.longDescription ?? '',
        membership: createLegacySpaceParams.membership,
        channel: {
            metadata: createLegacySpaceParams.channelName || '',
        },
    };
    const abstractAccountAddress = await getAbstractAccountAddress({
        rootKeyAddress: await getSignerAddress(signer),
        newAccountImplementationType: smartAccount.type,
        paymasterProxyUrl,
        paymasterProxyAuthSecret,
    });
    if (!abstractAccountAddress) {
        throw new Error('abstractAccountAddress is required');
    }
    const createSpaceFnName = 'createSpace';
    const callDataCreateSpace = spaceDapp.spaceRegistrar.LegacySpaceArchitect.encodeFunctionData(createSpaceFnName, [spaceInfo]);
    if (await spaceDapp.walletLink.checkIfLinked((await signer.getAddress()), abstractAccountAddress)) {
        const functionHashForPaymasterProxy = getFunctionSigHash(spaceDapp.spaceRegistrar.LegacySpaceArchitect.interface, createSpaceFnName);
        const op = await smartAccount.sendUserOp({
            toAddress: spaceDapp.spaceRegistrar.LegacySpaceArchitect.address,
            callData: callDataCreateSpace,
            signer,
            spaceId: undefined,
            functionHashForPaymasterProxy,
        });
        return op;
    }
    // wallet isn't linked, create a user op that both links and creates the space
    const functionName = 'createSpace_linkWallet';
    // TODO: this needs to accept an array of names/interfaces
    const functionHashForPaymasterProxy = getFunctionSigHash(spaceDapp.spaceRegistrar.LegacySpaceArchitect.interface, functionName);
    const callDataForLinkingSmartAccount = await encodeDataForLinkingSmartAccount(spaceDapp, signer, abstractAccountAddress);
    const op = await smartAccount.sendUserOp({
        toAddress: [
            spaceDapp.walletLink.address,
            spaceDapp.spaceRegistrar.LegacySpaceArchitect.address,
        ],
        callData: [callDataForLinkingSmartAccount, callDataCreateSpace],
        signer,
        spaceId: undefined,
        functionHashForPaymasterProxy,
    });
    return op;
}
